/* src/components/pages/Entidades.jsx */
/* Homologado a estándar Overwatch - versión PRO */
/* Última modificación: 18-03-2026 */

import { useEffect, useState, useCallback, useMemo } from "react";

import {
  Box,
  Button,
  Card,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  FormControlLabel,
  Checkbox
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";

import api from "../../services/api.js";

import { CustomToolbar } from "../../components/datagrid/CustomToolbar";
import DataGridSkeleton from "../../components/datagrid/DataGridSkeleton";

import { useAuth } from "../../context/AuthContext"; // token

import { useFeedback } from "../../context/FeedbackContext";

import { exportPdfAsync, downloadBlob } from "../utils/exportUtils";
import { validarRut } from "../utils/rutValidator";
import { normalizeRut } from "../utils/rutUtils";

import {
  RiFolderDownloadLine,
  RiShareBoxFill,
  RiFileEditLine,
} from "react-icons/ri";

/* Campos para exportar a Csv desde el backend */
const CSV_FIELDS = [
  { key: 'id', label: 'Id' },
  { key: 'tipoent', label: 'Tipo' },
  { key: 'rutcli', label: 'RUT' },
  { key: 'nombre', label: 'Nombre' },
  { key: 'direccion', label: 'Domicilio' },
  { key: 'telefono', label: 'Teléfono' },
  { key: 'email', label: 'Email' },
  { key: 'iban', label: 'Banco' },
  { key: 'ctacte', label: 'Ctacte' },
];

const Misentidades = () => {
  const { user } = useAuth();

  console.log('user:',user);

  const { showFeedback } = useFeedback();

  const [entidades, setEntidades] = useState([]);
  const [loading, setLoading] = useState(false);

  const [tipobus, setTipobus] = useState("TODOS");

  const [open, setOpen] = useState(false);
  const [editingEntidad, setEditingEntidad] = useState(null);

  const [id, setId] = useState(0);
  const [tipoent, setTipoent] = useState("");
  const [rut, setRut] = useState("");
  const [nombre, setNombre] = useState("");
  const [direccion, setDireccion] = useState("");
  const [telefono, setTelefono] = useState("");
  const [email, setEmail] = useState("");

  const [idban, setIdban] = useState(0);
  const [bancos, setBancos] = useState([]);
  const [ctacte, setCtacte] = useState("");

  const [errors, setErrors] = useState({});

  const [generandoPdf, setGenerandoPdf] = useState(false);

  const [openCsvDialog, setOpenCsvDialog] = useState(false);

  /* Estados para paginación desde el servidor */
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);

  /* Selecciona todos los campos para Csv */
  const [selectedCsvFields, setSelectedCsvFields] = useState(
    CSV_FIELDS.map(f => f.key)
  );

  const handleCloseModal = () => setOpen(false);

  /* ================= FETCH ENTIDADES ================= */

  const fetchEntidades = useCallback(async ({ page = 0, pageSize = 20 } = {}) => {

    setLoading(true);

    try {

      const response = await api.post("/entidades/getentidades", {
        tipobus,
        page,
        pageSize,
      });

      if (response.data.success && response.data.rows) {

        const { rows, total } = response.data;

        setEntidades(rows);
        setRowCount(total);

      } else {

        setEntidades([]);
        setRowCount(0);

      }

    } catch {

      setEntidades([]);
      setRowCount(0);

      showFeedback({
        message: "No fue posible obtener las entidades",
        severity: "error",
      });

    } finally {
      setLoading(false);
    }

  }, [tipobus, showFeedback]);

  /* Carga controlada (sin loop) */
  useEffect(() => {
    fetchEntidades({ page, pageSize });
  }, [fetchEntidades, page, pageSize]);


  /* ================= FETCH BANCOS ================= */

  const fetchBancos = useCallback(async () => {

    try {

      const response = await api.post("/bancos/getbancos", {
        idBan: 0
      });

      if (response.data.success) {
        setBancos(response.data.rows || []);
      } else {
        setBancos([]);
      }

    } catch {
      setBancos([]);
    }

  }, []);

  useEffect(() => {
    fetchBancos();
  }, [fetchBancos]);

  /* ================= EXPORT PDF ================= */

  const exportToPDF = async () => {
    exportPdfAsync({
      endpointBase: "/entidades/pdf",
      payload: {
        tipobus,
        usuario: user.usuario,
        rol: user.rol
      },
      onStart: () => setGenerandoPdf(true),
      onFinish: () => setGenerandoPdf(false),
      onError: (msg) => {
        setGenerandoPdf(false);
        showFeedback({
          message: msg,
          severity: "error"
        });
      }
    });
  };

  /* Helpers de Selección de campos para exportar a Csv */
  const toggleCsvField = (fieldKey) => {
    setSelectedCsvFields(prev =>
      prev.includes(fieldKey)
        ? prev.filter(k => k !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  /* ================= EXPORT CSV ================= */

  const exportCsv = async () => {
    try {
      const response = await api.post(
        "/entidades/csv",
        { tipobus,
          fields: selectedCsvFields,
         },
        { responseType: "blob" }
      );
      downloadBlob(response.data, "entidades.csv");
      setOpenCsvDialog(false);

    } catch {
      showFeedback({
        message: "No fue posible exportar el CSV",
        severity: "error"
      });
    }
  };

  /* ================= CRUD ================= */

  const handleEdit = (row) => {

    setEditingEntidad(row);

    setId(row.id);
    setTipoent(row.tipoent);
    setRut(row.rut);
    setNombre(row.nombre);
    setDireccion(row.direccion);
    setTelefono(row.telefono);
    setEmail(row.email);

    setIdban(row.idban || 0);
    setCtacte(row.ctacte || "");

    setErrors({});

    setOpen(true);

  };


  /* ================= SAVE ================= */

  const handleSave = async () => {

    const validationErrors = {};

    if (!rut) validationErrors.rut = "El Rut es obligatorio";

    if (rut && !validarRut(rut)) {
      validationErrors.rut = "El RUT no es válido";
    }

    if (!tipoent) validationErrors.tipoent = "Debe seleccionar el tipo";
    if (!rut) validationErrors.rut = "El Rut es obligatorio";
    if (!nombre) validationErrors.nombre = "El Nombre es obligatorio";

    if (idban > 0 && !ctacte) {
      validationErrors.ctacte =
        "Debe ingresar la cuenta corriente si selecciona banco";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {

      if (editingEntidad) {

        await api.post("/entidades/updateentidad", {
          id,
          tipoent,
          rut: normalizeRut(rut),
          nombre,
          direccion,
          telefono,
          email,
          idban,
          ctacte
        });

        showFeedback({
          message: "Entidad actualizada correctamente",
          severity: "success"
        });

      } else {

        await api.post("/entidades/addentidad", {
          tipoent,
          rut: normalizeRut(rut),
          nombre,
          direccion,
          telefono,
          email,
          idban,
          ctacte
        });

        showFeedback({
          message: "Entidad registrada correctamente",
          severity: "success"
        });

      }

      fetchEntidades();
      handleCloseModal();

    } catch {

      showFeedback({
        message: "No fue posible guardar la entidad",
        severity: "error"
      });

    }

  };

  /* ================= COLUMNS ================= */

  const columns = useMemo(() => [

    {
      field: "actions",
      headerName: "Acciones",
      width: 90,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",

      renderCell: (params) => {

        const row = params.row;

        return (
          <Stack direction="row" spacing={0.6}>

            <Button
              size="small"
              variant="contained"
              onClick={() => handleEdit(row)}
              sx={{
                backgroundColor: (theme) => theme.custom.ui.primaryAction,
                color: (theme) => theme.custom.ui.primaryActionText,
                minWidth: 0,
                padding: 0,
                width: 30,
                height: 22,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                "&:hover": (theme) => theme.custom.ui.primaryActionHover,
              }}
            >
              <RiFileEditLine size={18} />
            </Button>
          </Stack>
        );
      }
    },
    {
      field: "tipoent",
      headerName: "Tipo",
      width: 120,
      align: "center",
      headerAlign: "center",
      sortable: false,

      renderCell: (params) => {

        const tipo = params.value || "DEUDOR";

        return (

          <Box
            sx={(theme) => {

              const colors = {
                CESIONARIO: {
                  bg: theme.palette.success.main,
                  text: "#fff",
                },

                CEDENTE: {
                  bg: theme.palette.warning.main,
                  text: "#fff",
                },

                DEUDOR: {
                  bg: theme.palette.secondary.main,
                  text: "#fff",
                }
              };

              const color = colors[tipo] || colors.DEUDOR;

              return {
                px: 1.2,
                height: 22,
                minWidth: 95,
                borderRadius: 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.72rem",
                fontWeight: 600,
                backgroundColor: color.bg,
                color: color.text,
                letterSpacing: "0.02em"
              };

            }}
          >
            {tipo}
          </Box>
        );
      }
    },
    {
      field: "rut",
      headerName: "Rut",
      width: 140,
      align: "center",
      headerAlign: "center"
    },

    {
      field: "nombre",
      headerName: "Razón Social",
      width: 350,
      align: "center",
      headerAlign: "center"
    },

    {
      field: "direccion",
      headerName: "Dirección",
      width: 300,
      align: "center",
      headerAlign: "center"
    },

    {
      field: "telefono",
      headerName: "Teléfono",
      width: 200,
      align: "center",
      headerAlign: "center"
    },

    {
      field: "email",
      headerName: "Email",
      width: 250,
      align: "center",
      headerAlign: "center"
    }

  ], []);

  /* Render */
  return (
    <Box sx={{ width: "100%" }}>
      {/* CONTROLES */}
      <Stack direction="row" spacing={1} mb={1} flexWrap="wrap">

        {/* Selección dle Tipo */}
        <TextField
          select
          label="Tipo"
          value={tipobus}
          onChange={(e) => setTipobus(e.target.value)}
          sx={{
            minWidth: 200,
            flexShrink: 0,
            height: '40px', // Altura del TextField
            '& .MuiOutlinedInput-root': { height: '40px' },
            '& .MuiSelect-select': { textAlign: 'center' }
          }}
        >
          <MenuItem value="TODOS">TODOS</MenuItem>
          <MenuItem value="CESIONARIO">CESIONARIO</MenuItem>
          <MenuItem value="CEDENTE">CEDENTE</MenuItem>
          <MenuItem value="DEUDOR">DEUDOR</MenuItem>
        </TextField>


        {/* Botón Pdf */}
        <Button
          variant="contained"
          startIcon={<RiFolderDownloadLine size={18} />}
          disabled={!entidades.length}
          onClick={exportToPDF}
          sx={(theme) => ({
            minWidth: 110,
            height: 40,
            flexShrink: 0,
            backgroundColor: theme.custom.ui.exportActionBg,
            color: theme.custom.ui.exportActionText,
            border: `1px solid ${theme.custom.ui.exportActionBorder}`,
            "&:hover": {
              backgroundColor: theme.custom.ui.exportActionBgHover
            }
          })}
        >
          PDF
        </Button>

        {/* Botón Csv */}
        <Button
          variant="contained"
          startIcon={<RiShareBoxFill size={18} />}
          disabled={!entidades.length}
          onClick={() => setOpenCsvDialog(true)}
          sx={(theme) => ({
            minWidth: 110,
            height: 40,
            flexShrink: 0,
            backgroundColor: theme.custom.ui.exportActionBg,
            color: theme.custom.ui.exportActionText,
            border: `1px solid ${theme.custom.ui.exportActionBorder}`,
            "&:hover": {
              backgroundColor: theme.custom.ui.exportActionBgHover
            }
          })}
        >
          CSV
        </Button>

      </Stack>

      {/* Configuración MIU DataGrid */}
      <Box sx={{ height: "calc(100vh - 210px)" }}>

        <DataGrid          rows={entidades}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          density="compact"
          rowHeight={38}
          headerHeight={40}
          disableRowSelectionOnClick

          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={{ page, pageSize }}

          onPaginationModelChange={({ page: newPage, pageSize: newPageSize }) => {
            const isPageSizeChange = newPageSize !== pageSize;

            const finalPage = isPageSizeChange ? 0 : newPage;

            setPage(finalPage);
            setPageSize(newPageSize);

            fetchEntidades({
              page: finalPage,
              pageSize: newPageSize,
            });
          }}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          slots={{
            toolbar: CustomToolbar,
            loadingOverlay: DataGridSkeleton,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 400 },
            },
          }}
          sx={{
            height: '100%',
            flex: 1,
            width: '100%',
            minWidth: 720,      // scroll horizontal cuando corresponde
            border: 'none',
            // accesibilidad / foco
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
          }}
        />
      </Box>

      {/* Modal para ingresar o modificar */}
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            handleCloseModal();
          }
        }}
        disableEnforceFocus
        disableRestoreFocus
        fullWidth={false}
        maxWidth={false}
      >
        <Card
          sx={{
            width: {
              xs: "95vw",
              sm: "90vw",
              md: 600,
            },

            height: "75vh",
            display: "flex",
            flexDirection: "column",

            borderRadius: 2,
            border: (theme) => `1px solid ${theme.custom.ui.borderSubtle}`,
            boxShadow: (theme) => theme.custom.ui.modalShadow,
            overflow: "hidden",
          }}
        >

          {/* HEADER */}
          <Box px={3} pt={2} pb={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={600}>
                {editingEntidad ? "Editar entidad" : "Agregar entidad"}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                ▶️ Overwatch
              </Typography>
            </Stack>
          </Box>

          <Divider />

          {/* CONTENIDO (SCROLL) */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              px: 3,
              py: 2,
            }}
          >
            <Stack spacing={2}>

              <TextField
                select
                label="Tipo"
                value={tipoent}
                onChange={(e) => setTipoent(e.target.value)}
                error={Boolean(errors.tipoent)}
                helperText={errors.tipoent}
                sx={fieldSx}
              >
                <MenuItem value="CESIONARIO">CESIONARIO</MenuItem>
                <MenuItem value="CEDENTE">CEDENTE</MenuItem>
                <MenuItem value="DEUDOR">DEUDOR</MenuItem>
              </TextField>


              <TextField
                label="Rut"
                value={rut}
                onChange={(e) => {

                  const value = e.target.value;

                  setRut(value);

                  if (value && !validarRut(value)) {

                    setErrors((prev) => ({
                      ...prev,
                      rut: "El RUT no es válido"
                    }));

                  } else {

                    setErrors((prev) => ({
                      ...prev,
                      rut: ""
                    }));

                  }

                }}
                error={Boolean(errors.rut)}
                helperText={errors.rut}
                disabled={Boolean(editingEntidad)}
                sx={fieldSx}
              />

              <TextField
                label="Nombre"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                error={Boolean(errors.nombre)}
                helperText={errors.nombre}
                sx={fieldSx}
              />

              <TextField
                label="Dirección"
                value={direccion}
                onChange={(e) => setDireccion(e.target.value)}
                sx={fieldSx}
              />

              <TextField
                label="Teléfono"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                sx={fieldSx}
              />

              <TextField
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={fieldSx}
              />

              <FormControl fullWidth error={Boolean(errors.idban)} sx={fieldSx}>
                <InputLabel>Banco</InputLabel>

                <Select
                  value={idban}
                  label="Banco"
                  onChange={(e) => setIdban(e.target.value)}
                >
                  <MenuItem value={0}>Seleccionar Banco</MenuItem>

                  {bancos.map((banco) => (
                    <MenuItem key={banco.id} value={banco.id}>
                      {banco.nomban}
                    </MenuItem>
                  ))}
                </Select>

                {errors.idban && (
                  <FormHelperText>{errors.idban}</FormHelperText>
                )}
              </FormControl>

              <TextField
                label="Cuenta corriente"
                value={ctacte}
                onChange={(e) => setCtacte(e.target.value)}
                error={Boolean(errors.ctacte)}
                helperText={errors.ctacte}
                disabled={!idban}
                sx={fieldSx}
              />

            </Stack>
          </Box>

          <Divider />

          {/* FOOTER */}
          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
            }}
          >

            <Button
              variant="outlined"
              onClick={handleCloseModal}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              onClick={handleSave}
              sx={(theme) => ({
                backgroundColor: theme.custom.ui.primaryActionBg,
                color: theme.custom.ui.primaryActionText,

                "&:hover": {
                  backgroundColor: theme.custom.ui.primaryActionBgHover
                }
              })}
            >
              {editingEntidad ? "Actualizar" : "Guardar"}
            </Button>

          </Box>

        </Card>
      </Dialog>


      {/* Dialogo generando pdf */}
      <Dialog
        open={generandoPdf}
        aria-labelledby="generando-pdf-title"
        aria-describedby="generando-pdf-desc"

        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: (theme) => ({
              borderRadius: 3,
              textAlign: 'center',
              backgroundColor: theme.custom.ui.modalBg,
              boxShadow: theme.custom.ui.modalShadow,
            }),
          },
        }}
      >
        <DialogContent>
          <Stack spacing={2} alignItems="center" py={2}>
            <CircularProgress
              size={42}
              thickness={4}
              sx={{
                color: (theme) => theme.custom.ui.focus,
              }}
            />

            <Typography id="generando-pdf-title" variant="body2">
              Generando reporte
            </Typography>

            <Typography
              id="generando-pdf-desc"
              variant="caption"
              color="text.secondary"
            >
              Estamos trabajando en ello…
            </Typography>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Dialogo selección de campos para Csv desde el backend */}
      <Dialog
        open={openCsvDialog}
        onClose={() => setOpenCsvDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              mb: 0,
              color: (theme) => theme.custom.ui.brandAccent,
              fontSize: '1.4rem'
            }}>
            Exportar CSV
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent>
          <Box sx={{ mb: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
            Selecciona los campos que deseas incluir en el archivo.
          </Box>

          <Stack spacing={0.5}>
            {CSV_FIELDS.map(field => (
              <FormControlLabel
                key={field.key}
                control={
                  <Checkbox
                    checked={selectedCsvFields.includes(field.key)}
                    onChange={() => toggleCsvField(field.key)}
                  />
                }
                label={field.label}
              />
            ))}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenCsvDialog(false)}
            sx={{
              backgroundColor: (theme) => theme.custom.ui.secondaryActionBg,
              color: (theme) => theme.custom.ui.secondaryActionText,
              '&:hover': {
                backgroundColor: (theme) => theme.custom.ui.secondaryActionBgHover,
              },
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            disabled={selectedCsvFields.length === 0}
            onClick={exportCsv}
            sx={{
              backgroundColor: (theme) => theme.custom.ui.primaryAction,
              color: (theme) => theme.custom.ui.primaryActionText,
              '&:hover': {
                backgroundColor: (theme) => theme.custom.ui.primaryActionHover,
              },
            }}
          >
            Exportar
          </Button>

        </DialogActions>
      </Dialog>
    </Box>
  );
};

const fieldSx = {
  '& .MuiInputBase-root': {
    height: 34,
    fontSize: 13.5,
  },

  '& .MuiInputLabel-root': {
    fontSize: 12.5,
  },

  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    fontSize: '0.7rem',
    minHeight: '1em',
  },
};

export default Misentidades;
